import React, { FC, useCallback, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { StepDots } from '../components/StepDots';
import { InputField } from '../../pro/applicationForm/input';
import { CustomFieldLabel } from '../../../components/fields/CustomFieldLabel/CustomFieldLabel';
import { CustomSelectField } from '../../pro/applicationForm/customSelect';
import InlineBrandSelector from '../../videoProduction/components/InlineBrandSelector';
import { SlackChannelPicker } from '../../../components/SlackChannelPicker/SlackChannelPicker';
import { SOWItem } from '../../../hooks/useOrderFormData';
import { formatCurrency } from '../../../utils/formatCurrency';
import { useUser } from '../../../hooks/useUser';
import { hideAllPricing } from '../../../utils';

interface OrderDetailsStepProps {
    values: any;
    errors: any;
    touched: any;
    setFieldValue: (field: string, value: any) => void;
    isBrandRole: boolean;
    isInternalRole: boolean;
    selectedBrand: any;
    setSelectedBrand: (brand: any) => void;
    setSelectedChannels: (channels: any[]) => void;
    setSelectedContentTypeId: (id: number | null) => void;
    sowOptions: { label: string; value: number }[];
    sowsLoading: boolean;
    slackChannels?: { label: string; value: string }[];
    slackChannelsLoading?: boolean;
    selectedSow?: SOWItem | null;
    mode: "create" | "edit";
    onNext: () => void;
    onCancel: () => void;
    onIntakeFile?: (file: File) => void;
    intakeBusy?: boolean;
    intakeStageIndex?: number;
    intakeFileName?: string;
    intakeSummary?: string;
    // Supplier name from a dropped brief that matched no brand. Created on
    // submit unless a brand is picked first.
    pendingBrandName?: string;
}

const INTAKE_STAGES = [
    "Uploading the file",
    "Reading the spreadsheet",
    "Pulling out the order, products and cuts",
    "Matching the retailer, supplier brand, channels and content types",
];

const IntakeProgress: FC<{ fileName: string; stageIndex: number }> = ({
    fileName,
    stageIndex,
}) => {
    const done = Math.max(0, stageIndex);
    const percent = Math.round(((done + 0.5) / INTAKE_STAGES.length) * 100);
    return (
        <Box
            className="w-full rounded-xl border border-[#EAD9B8] bg-tint overflow-hidden"
            sx={{ "@media (prefers-reduced-motion: reduce)": { "& .intake-spin": { animation: "none" } } }}
        >
            <Box className="flex items-center gap-2.5 px-4 pt-3.5 pb-2.5">
                <Box className="relative w-7 h-7 shrink-0">
                    <Box className="absolute inset-0 rounded-full border-2 border-[#EAD9B8]" />
                    <Box className="intake-spin absolute inset-0 rounded-full border-2 border-transparent border-t-[var(--accent)] animate-spin" />
                </Box>
                <Box className="min-w-0">
                    <Typography
                        className="truncate"
                        sx={{ fontSize: 13, fontWeight: 700, color: "var(--text)", fontFamily: "CircularStd" }}
                    >
                        {fileName || "Reading the brief"}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "var(--accent-text)", fontFamily: "CircularStd" }}>
                        This usually takes about half a minute
                    </Typography>
                </Box>
            </Box>

            <Box component="ul" className="px-4 pb-3 m-0 list-none flex flex-col gap-1.5">
                {INTAKE_STAGES.map((label, index) => {
                    const isDone = index < stageIndex;
                    const isActive = index === stageIndex;
                    return (
                        <Box
                            component="li"
                            key={label}
                            className="flex items-center gap-2"
                            sx={{ opacity: isDone || isActive ? 1 : 0.45, transition: "opacity .25s" }}
                        >
                            <Box
                                className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                                    isActive ? "animate-pulse" : ""
                                }`}
                                sx={{
                                    backgroundColor: isDone || isActive ? "var(--accent)" : "transparent",
                                    border: isDone || isActive ? "none" : "1.5px solid var(--muted)",
                                }}
                            >
                                {isDone && <CheckIcon sx={{ fontSize: 11, color: "#fff" }} />}
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontFamily: "CircularStd",
                                    fontWeight: isActive ? 700 : 500,
                                    color: isActive ? "var(--text)" : "var(--text-2)",
                                }}
                            >
                                {label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            <Box className="h-1 w-full" sx={{ backgroundColor: "rgba(0,0,0,.06)" }}>
                <Box
                    className="h-full"
                    sx={{
                        width: `${percent}%`,
                        backgroundColor: "var(--accent)",
                        transition: "width .6s ease",
                    }}
                />
            </Box>
        </Box>
    );
};

export const OrderDetailsStep: FC<OrderDetailsStepProps> = ({
    values,
    errors,
    touched,
    setFieldValue,
    isBrandRole,
    isInternalRole,
    selectedBrand,
    setSelectedBrand,
    setSelectedChannels,
    setSelectedContentTypeId,
    sowOptions,
    sowsLoading,
    slackChannels,
    slackChannelsLoading,
    selectedSow = null,
    mode,
    onNext,
    onCancel,
    onIntakeFile,
    intakeBusy = false,
    intakeStageIndex = -1,
    intakeFileName = "",
    intakeSummary = "",
    pendingBrandName = "",
}) => {
    const intakeInputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);
    const takeFile = (files: FileList | null) => {
        const file = files && files[0];
        if (file && onIntakeFile && !intakeBusy) onIntakeFile(file);
    };
    const isRMN = selectedBrand?.rmn;
    const { user, role } = useUser();
    // RMN pricing hide (TDC-1591): an rmn:true brand's order shows no
    // package price — internal staff included.
    const pricingHidden = hideAllPricing(role, user, { brand: selectedBrand });
    const nextDisabled = !values.order_name?.trim() || (!isBrandRole && !values.brand && !pendingBrandName);

    const handleBrandChange = useCallback(
        (field: string, value: any) => {
            const resetBrandDependentFields = (brandValue: any) => {
                setFieldValue("brand", brandValue);
                setFieldValue("sow", null);
                setFieldValue("slack_channel", "");
                setFieldValue("create_slack_channel", false);
                setFieldValue("new_slack_channel_name", "");
                setFieldValue("new_content_type", "");
                setFieldValue("new_channel", "");
                setFieldValue("order_content_type", "");
                setFieldValue("order_rmn_brand", null);
                setSelectedChannels([]);
                setSelectedContentTypeId(null);
                setSelectedBrand(brandValue);
            };
            if (field === "brands") {
                resetBrandDependentFields(value?.[0] || null);
            } else if (field === "brand") {
                const brandValue = value === "" || value == null ? null : value;
                if (brandValue === null || typeof brandValue !== "string") {
                    resetBrandDependentFields(brandValue);
                }
            } else {
                setFieldValue(field, value);
            }
        },
        [setFieldValue, setSelectedBrand, setSelectedChannels, setSelectedContentTypeId]
    );

    return (
        <>
            {/* Scrollable body */}
            <Box sx={{ flex: 1, overflowY: "auto", p: { xs: "20px 16px 22px", sm: "22px 36px 24px" } }}>
                <Box mb="6px">
                    <StepDots step={1} />
                </Box>

                <Box display="flex" alignItems="center" gap="10px" mb="6px">
                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: "var(--text)",
                            letterSpacing: -0.4,
                            fontFamily: "CircularStd",
                        }}
                    >
                        Order Details
                    </Typography>
                    {isRMN && (
                        <Chip
                            label="RMN ORDER"
                            size="small"
                            sx={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: "var(--accent-text)",
                                background: "var(--tint)",
                                borderRadius: "99px",
                                letterSpacing: 0.6,
                                height: 20,
                                fontFamily: "CircularStd",
                            }}
                        />
                    )}
                </Box>
                <Typography sx={{ fontSize: 13.5, color: "var(--text-2)", mb: "20px", fontFamily: "CircularStd" }}>
                    {isRMN
                        ? "Set up the retail-media order. You'll pick the supplier brand for each project on the next step."
                        : "Set up the order, then add one or more video projects to it."}
                </Typography>

                {onIntakeFile && (
                    <Box sx={{ mb: "20px" }}>
                        <input
                            ref={intakeInputRef}
                            type="file"
                            accept=".xlsx,.xlsm"
                            hidden
                            onChange={(e) => takeFile(e.target.files)}
                        />
                        {intakeBusy ? (
                            <IntakeProgress fileName={intakeFileName} stageIndex={intakeStageIndex} />
                        ) : (
                            <button
                                type="button"
                                disabled={intakeBusy}
                                onClick={() => intakeInputRef.current?.click()}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    if (!intakeBusy) setDragging(true);
                                }}
                                onDragLeave={() => setDragging(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setDragging(false);
                                    takeFile(e.dataTransfer.files);
                                }}
                                className={`w-full flex flex-col items-center justify-center gap-1 px-4 py-5 rounded-xl border border-dashed transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                                    dragging
                                        ? "border-[var(--accent)] bg-tint"
                                        : "border-[#EAD9B8] bg-surface hover:bg-tint"
                                }`}
                            >
                                <span className="text-[12.5px] font-semibold text-accent-text">
                                        Drop an RMN, agency or brand brief to fill this in
                                </span>
                                <span className="text-[11.5px] text-muted text-center">
                                        {intakeSummary ||
                                        ".xlsx or .xlsm from a retail media network, agency or supplier brand. Everything stays editable, and nothing is created until you submit the order."}
                                </span>
                            </button>
                        )}
                    </Box>
                )}

                <Box display="flex" flexDirection="column" gap="16px">
                    <InputField name="order_name" label="Order Name" placeholder="e.g. Summer Campaign 2026" />

                    {!isBrandRole && (
                        <div>
                            <div className="flex items-center gap-[6px] mb-1">
                                <CustomFieldLabel required>
                                    {isRMN ? "Retail Media Network" : "Brand"}
                                </CustomFieldLabel>
                            </div>
                            <InlineBrandSelector
                                setFieldValue={handleBrandChange}
                                isRMN={false}
                                showRmnToggle={false}
                                selectedBrand={selectedBrand}
                                setSelectedBrand={(brand: any) => {
                                    setSelectedBrand(brand);
                                    setFieldValue("brand", brand);
                                }}
                                disabled={false}
                                filterRmn={undefined}
                                allowAdd={false}
                            />
                            {touched.brand && errors.brand && (
                                <Typography color="error" variant="caption">
                                    {String(errors.brand)}
                                </Typography>
                            )}
                            {pendingBrandName && (
                                <Typography
                                    sx={{ mt: "6px", fontSize: 12, color: "var(--accent-text)", fontFamily: "CircularStd" }}
                                >
                                    No brand named "{pendingBrandName}" exists. It will be created when you submit
                                    the order, or pick an existing brand above.
                                </Typography>
                            )}
                        </div>
                    )}

                    {!isBrandRole && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <CustomSelectField
                                    name="sow"
                                    label="SOW"
                                    placeholder={
                                        sowsLoading
                                            ? "Loading SOWs..."
                                            : selectedBrand
                                            ? "Select SOW"
                                            : "Select a brand first"
                                    }
                                    options={sowOptions}
                                    required={false}
                                    noLabel={false}
                                    disabled={!selectedBrand || sowsLoading}
                                />
                                {selectedSow?.package_snapshot && !pricingHidden && (
                                    <Typography
                                        sx={{
                                            mt: 0.75,
                                            fontSize: "12px",
                                            color: "var(--accent-text)",
                                            fontFamily: "CircularStd",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        <b>{selectedSow.package_snapshot.display_name}</b> package —
                                        billed flat{" "}
                                        {formatCurrency(selectedSow.package_snapshot.sell_price)} ·{" "}
                                        {selectedSow.package_usage?.skus_used ?? 0} of{" "}
                                        {selectedSow.package_snapshot.max_skus} SKUs used
                                    </Typography>
                                )}
                            </Grid>
                            {isInternalRole && (
                                <Grid item xs={12} sm={6}>
                                    <SlackChannelPicker
                                        channels={slackChannels}
                                        channelsLoading={slackChannelsLoading}
                                        name="slack_channel"
                                        createFlagName="create_slack_channel"
                                        createNameFieldName="new_slack_channel_name"
                                        hideCreate={mode === "edit"}
                                        brand={
                                            selectedBrand
                                                ? {
                                                      name: selectedBrand.name,
                                                      rmn: !!selectedBrand.rmn,
                                                      slack_channel: selectedBrand.slack_channel,
                                                  }
                                                : null
                                        }
                                        placeholder={
                                            selectedBrand ? "Select Slack Channel" : "Select a brand first"
                                        }
                                        disabled={!selectedBrand}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    )}

                    {/* Order-Level Public Page (internal only) */}
                    {isInternalRole && (
                        <Box
                            sx={{
                                padding: "14px 16px",
                                background: "var(--surface-2)",
                                borderRadius: "12px",
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.download_page}
                                        onChange={(e) => setFieldValue("download_page", e.target.checked)}
                                        sx={{
                                            color: "var(--muted)",
                                            "&.Mui-checked": { color: "var(--accent)" },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: 13.5,
                                                fontWeight: 500,
                                                fontFamily: "CircularStd",
                                                color: "var(--text)",
                                            }}
                                        >
                                            Order-Level Public Page
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: "var(--text-2)",
                                                fontFamily: "CircularStd",
                                                mt: 0.25,
                                            }}
                                        >
                                            Enable a public page for this order — brand stakeholders can review without
                                            an account.
                                        </Typography>
                                    </Box>
                                }
                                sx={{ alignItems: "flex-start", ml: 0 }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: { xs: "12px 18px", sm: "14px 28px" },
                    borderTop: "1px solid var(--border)",
                    background: "var(--surface)",
                    flexShrink: 0,
                }}
            >
                <Button
                    onClick={onCancel}
                    sx={{
                        textTransform: "capitalize",
                        fontFamily: "CircularStd",
                        color: "var(--text)",
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={nextDisabled}
                    endIcon={<ArrowForwardIcon />}
                    onClick={onNext}
                    sx={{
                        textTransform: "capitalize",
                        fontFamily: "CircularStd",
                        borderRadius: "24px",
                        px: 3,
                    }}
                >
                    Next
                </Button>
            </Box>
        </>
    );
};
